import React from "react";
import { useNavigate } from "react-router-dom";
import {
Search,CircleStar,ShieldCheck,Lightbulb
} from "lucide-react";
import WorkflowSection from "../components/layout/about/workflow";
import Header from "../components/layout/header/Header";
import Footer from "../components/layout/footer/Footer";
import LaunchSection from "../components/layout/launch/LaunchSection";
import { Helmet } from "react-helmet-async";
import "../components/layout/about/about.css";

interface TeamMember {
  name: string;
  role: string;
  title: string;
  description: string;
  imageUrl: string;
}

const TEAM_MEMBERS: TeamMember[] = [
  {
    name: "Utkarsh Wagh",
    role: "CHIEF EXECUTIVE OFFICER",
    title: "Founder & CEO - Teamcast |",
    description: "Humancloud | Entrepreneurship",
    imageUrl: "/images/team/utkarsh.jpg" // Swap with your source asset path
  },
  {
    name: "Vitesh Jaiswal",
    role: "CHIEF TECHNICAL OFFICER",
    title: "Leading our technical vision and",
    description: "implementation strategy",
    imageUrl: "/images/team/vitesh.jpg"
  },
  {
    name: "John Gengarella",
    role: "ADVISOR",
    title: "Providing strategic guidance",
    description: "and industry insights",
    imageUrl: "/images/team/john.jpg"
  },
  {
    name: "Tanvir Shaikh",
    role: "VP SALES",
    title: "Driving revenue growth and",
    description: "building strategic client partnerships",
    imageUrl: "/images/team/tanvir.jpg"
  }
];
const pageTitle =
  "About AIGIO | Research-First AI Brand Intelligence Platform";

const pageDescription =
  "Learn how AIGIO transforms businesses into launch-ready brands using AI-powered market research, strategy, branding, messaging, and marketing automation.";

const canonicalUrl = "https://aigio.ai/about";
const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  


  return (
    <div className="page-shell page-shell--about">
      {/* Dynamic Metadata SEO Injection */}
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Recommended: Open Graph metadata for social media sharing */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Header />

      <main className="about-page">
        {/* HERO */}
        <section className="about-hero">
          <div className="about-container">
            <span className="about-badge">ABOUT AIGIO</span>
            <h1 className="about-title">
              The AI brand manager
              <br />
              <span>Your brand, built on research—not prompts.</span>
            </h1>
            <p className="about-subtitle">
              AIGIO transforms business ideas into launch-ready brands through a structured, research-first workflow. From market analysis and brand strategy to identity, messaging, campaigns, and content, every recommendation is grounded in your business—not generic AI prompts.
            </p>
            <div className="about-actions">
              <button
  className="about-primary-btn"
  onClick={() => navigate("/#pricing")}
>
  Start Building Your Brand
</button>
              <button
  className="about-secondary-btn"
  onClick={() => navigate("/#audit")}
>
 Get an AI Brand Audit
</button>
            </div>
          </div>
        </section>

        {/* MISSION / THESIS */}
        <section className="about-thesis-section">
          <div className="about-thesis-grid">
            {/* Left: Thesis Statement Text */}
            <div className="about-thesis-content">
              <div className="thesis-heading-wrapper">
                <h2>WHY AIGIO</h2>
              </div>
              <p>
                Most businesses piece together branding through agencies, freelancers, AI tools, and endless revisions. The result is a disconnected brand that lacks consistency and direction.
AIGIO replaces that fragmented process with one intelligent workflow. It researches your market, builds your strategy, creates your brand identity, and generates launch-ready campaigns—keeping every decision connected from start to finish.

              </p>
            </div>

            {/* Right: Key Stat Card Indicator */}
            <div className="about-thesis-card">
              <div className="about-stat">
                <h3>10x Faster</h3>
                <h4>Brand Development</h4>
                <p>Move from business idea to a research-backed brand strategy, identity, and launch assets in hours instead of weeks.</p>
              </div>
            </div>
          </div>
        </section>
        <div className="divider" aria-hidden="true"></div>

        <LaunchSection />

        {/* METRICS BAR */}
        <div className="metrics-bar-container">
          <div className="metrics-bar-content">
            {/* Metric 1 */}
            <div className="metric-item">
              <span className="metric-number">18</span>
              <span className="metric-label">Strategy Components</span>
            </div>

            {/* Vertical Divider */}
            <div className="metric-divider" aria-hidden="true"></div>

            {/* Metric 2 */}
            <div className="metric-item">
              <span className="metric-number">13</span>
              <span className="metric-label">Campaign Types</span>
            </div>

            {/* Vertical Divider */}
            <div className="metric-divider" aria-hidden="true"></div>

            {/* Metric 3 */}
            <div className="metric-item">
              <span className="metric-number">7</span>
              <span className="metric-label">Identity Sections</span>
            </div>
          </div>
        </div>

        <WorkflowSection />

        {/* VISION SECTION */}
        <section className="vision-container">
          <div className="vision-header">
            <h2 className="vision-title">Why Businesses Choose AIGIO?</h2>
            <p className="vision-subtitle">
              Stop switching between AI tools, agencies, documents, and spreadsheets. AIGIO brings research, strategy, branding, content, and collaboration together in one connected platform built for modern businesses.
            </p>
          </div>

          <div className="vision-grid">
            {[
             {
                title: "Research-First Intelligence",
                icon: Search,
                description: "Every recommendation begins with market research, competitor analysis, and audience insights—not generic prompts."
              },
              {
                title: "Strategic Decision Making",
                icon: Lightbulb,
                description: "AI helps you make smarter branding decisions with structured strategies, positioning frameworks, and messaging that fit your business."
              },
              {
                title: "Brand Consistency",
                icon: CircleStar,
                description: "Keep every campaign, asset, and piece of content aligned with your approved brand identity and strategy."
              },
              {
                title: "Launch Faster",
                icon: ShieldCheck,
                description: "From business idea to launch-ready campaigns in hours instead of weeks—without sacrificing quality."
              }            ].map((item, index) => (
              <div key={index} className="vision-card">
                <div className="vision-card-icon">
                  {item.icon && <item.icon />}
                </div>
                <h3 className="vision-card-title">{item.title}</h3>
                <p className="vision-card-description">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

     <section className="aigio-team-section">
      <div className="aigio-team-container">
        
        <div className="aigio-team-header">
          <h2 className="aigio-team-title">Our Team</h2>
          <p className="aigio-team-subtitle">
            Sed ut perspiciatis unde omnis iste natus error sit voluptatem accusantium doloremque laudantium, totam rem aperiam, eaque ipsa quae ab illo inventore veritatis et quasi architecto beatae vitae dicta sunt explicabo.
          </p>
        </div>

        <div className="aigio-team-grid">
          {TEAM_MEMBERS.map((member, index) => (
            <div key={index} className="aigio-team-card">
              <div className="aigio-team-avatar-wrapper">
                <img 
                  src={member.imageUrl} 
                  alt={member.name} 
                  className="aigio-team-avatar" 
                />
              </div>
              <h3 className="aigio-team-member-name">{member.name}</h3>
              <span className="aigio-team-member-role">{member.role}</span>
              <p className="aigio-team-member-bio">
                {member.title}
                <br />
                {member.description}
              </p>
            </div>
          ))}
        </div>

      </div>
    </section>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;